// Pixel-faithful PDF export — mirrors the original index.html exporter.
// Each .ds-page node is rasterised and placed on a PDF page of its own size
// (A4 794x1123px for AU, Letter 816x1056px for US), so nothing is stretched.
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { buildVectorPdf } from './usds/vectorPdf';

const settle = (ms = 60) => new Promise(r => setTimeout(r, ms));

async function pagesFrom(root) {
  if (!root) return [];
  const found = Array.from(root.querySelectorAll('.ds-page'));
  return found.length ? found : (root.classList && root.classList.contains('ds-page') ? [root] : []);
}

async function buildPdf(root, meta = {}) {
  const pages = await pagesFrom(root);
  if (!pages.length) return null;
  try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch (e) { console.warn('Font readiness wait failed; exporting anyway', e); }
  await settle();

  // Print quality for the raster fallback: PNG (lossless) at 4x for a single
  // datasheet (~384dpi); big packs stay at 3x so the browser does not run out
  // of canvas memory.
  const scale = pages.length > 6 ? 3 : 4;
  const rasterize = async (page) => {
    const canvas = await html2canvas(page, { scale, backgroundColor: '#ffffff', useCORS: true, logging: false });
    return { dataUrl: canvas.toDataURL('image/png') };
  };

  // Vector first: AU datasheets (Helvetica = Arial metrics) and US kit pages (Archivo)
  // come out as real text, lines and SVG artwork. Proposal covers, or any page whose
  // vector pass throws, are rasterised so an export never fails outright.
  try { return await buildVectorPdf(pages, meta, rasterize); } catch (e) { console.warn('Vector export failed; falling back to raster', e); }

  const mm = (px) => (px / 96) * 25.4;
  const dims = pages.map(p => [mm(p.offsetWidth), mm(p.offsetHeight)]);
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: dims[0] });
  if (meta.title) pdf.setProperties({ title: meta.title, subject: meta.subject || '' });
  for (let i = 0; i < pages.length; i++) {
    const [W, H] = dims[i];
    const { dataUrl } = await rasterize(pages[i]);
    if (i > 0) pdf.addPage([W, H], 'portrait');
    // 8th arg is the zlib level for the embedded stream, not an image codec:
    // 'FAST' keeps PNG lossless but deflates it (~5-10x smaller file).
    pdf.addImage(dataUrl, 'PNG', 0, 0, W, H, undefined, 'FAST');
  }
  return pdf;
}

export async function exportPagesToPdf(root, filename = 'datasheet.pdf', meta = {}) {
  const pdf = await buildPdf(root, meta);
  if (pdf) pdf.save(filename);
}

export async function exportPagesToPdfBytes(root, meta = {}) {
  const pdf = await buildPdf(root, meta);
  return pdf ? pdf.output('arraybuffer') : null;
}
